import { initializePaddle, type Paddle } from '@paddle/paddle-js';
import { type SupabaseClient } from '@supabase/supabase-js';
import { authAudienceForApplication, createAuthClient } from '@ai-factory/auth-sdk';
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import type {
  BillingApplicationCode,
  CatalogProduct,
  CheckoutStatusPayload,
  SubscriptionAccess,
} from '@ai-factory/billing-sdk';
import type { AppBrand } from '@ai-factory/ui-core';
import {
  browserLocaleContext,
  createAppTranslator,
  formatDateTime,
  formatNumber,
} from '@ai-factory/i18n';
import { resolveApplicationFailure } from '@ai-factory/ui-core';

import { formatPrice, intervalLabel, resolveAppAccountId } from './model';
import './styles.css';

export type BillingScreen = 'pricing' | 'billing' | 'checkout-result' | 'past-due' | 'cancel';

export interface BillingRuntimeConfig {
  applicationCode: BillingApplicationCode;
  supabaseUrl: string;
  supabasePublishableKey: string;
  paddleClientToken?: string;
  paddleEnvironment?: 'sandbox' | 'production';
  siteUrl: string;
  locale?: string;
  timezone?: string;
}

interface BillingPageProps {
  screen: BillingScreen;
  config: BillingRuntimeConfig;
  brand: AppBrand;
  embedded?: boolean | undefined;
}

type ApiPayload = Record<string, unknown>;
type CreditAccount = { wallets?: Array<{ credit_type: string; available_balance: number }> };

class BillingRequestError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

function billingText(locale?: string) {
  return createAppTranslator('billing', locale ?? browserLocaleContext().locale);
}

function productTypeLabel(productType: string, t: ReturnType<typeof billingText>): string {
  const key = `billing.product_${productType}`;
  const value = t(key);
  return value === key ? productType.replaceAll('_', ' ') : value;
}

export function publicEntitlementFeatures(
  entitlements: Record<string, unknown>,
  locale: string,
  t: ReturnType<typeof billingText>,
): string[] {
  const features: string[] = [];
  const add = (feature: string) => {
    if (!features.includes(feature)) features.push(feature);
  };
  const monthlyCredits = Number(entitlements.monthly_image_credits ?? 0);
  if (monthlyCredits > 0) {
    add(t('billing.feature_monthly_image_credits', {
      count: formatNumber(monthlyCredits, locale),
    }));
  }
  const monthlyVisualCredits = Number(entitlements.monthly_visual_credits ?? 0);
  if (monthlyVisualCredits > 0) {
    add(t('billing.feature_monthly_visual_credits', {
      count: formatNumber(monthlyVisualCredits, locale),
    }));
  }
  const monthlyAssistantMessages = Number(entitlements.monthly_assistant_messages ?? 0);
  if (monthlyAssistantMessages > 0) {
    add(t('billing.feature_monthly_assistant_messages', {
      count: formatNumber(monthlyAssistantMessages, locale),
    }));
  }
  const maximumBrands = Number(entitlements.max_brands ?? 0);
  if (maximumBrands > 0) add(t(maximumBrands === 1
    ? 'billing.feature_one_brand'
    : 'billing.feature_max_brands', { count: formatNumber(maximumBrands, locale) }));
  const maximumProducts = Number(entitlements.max_products ?? 0);
  if (maximumProducts > 0) {
    add(t('billing.feature_max_products', {
      count: formatNumber(maximumProducts, locale),
    }));
  }
  const maximumMembers = Number(entitlements.max_members ?? 0);
  if (maximumMembers > 0) add(t(maximumMembers === 1
    ? 'billing.feature_one_member'
    : 'billing.feature_max_members', { count: formatNumber(maximumMembers, locale) }));
  const maximumWebsites = Number(entitlements.max_websites ?? 0);
  if (maximumWebsites > 0) add(t(maximumWebsites === 1
    ? 'billing.feature_one_website'
    : 'billing.feature_max_websites', { count: formatNumber(maximumWebsites, locale) }));
  const maximumAgents = Number(entitlements.max_agent_members ?? 0);
  if (maximumAgents > 0) add(t(maximumAgents === 1
    ? 'billing.feature_one_agent'
    : 'billing.feature_max_agents', { count: formatNumber(maximumAgents, locale) }));
  const maximumKnowledgeDocuments = Number(entitlements.max_knowledge_documents ?? 0);
  if (maximumKnowledgeDocuments > 0) add(t('billing.feature_max_knowledge_documents', {
    count: formatNumber(maximumKnowledgeDocuments, locale),
  }));
  const maximumKnowledgeStorage = Number(entitlements.max_knowledge_storage_mb ?? 0);
  if (maximumKnowledgeStorage > 0) add(t('billing.feature_max_knowledge_storage', {
    count: formatNumber(maximumKnowledgeStorage, locale),
  }));
  const retentionDays = Number(entitlements.conversation_retention_days ?? 0);
  if (retentionDays > 0) add(t('billing.feature_conversation_retention', {
    count: formatNumber(retentionDays, locale),
  }));
  const maximumResolution = entitlements.max_resolution;
  if (typeof maximumResolution === 'string') {
    add(t('billing.feature_max_resolution', {
      resolution: maximumResolution.toUpperCase(),
    }));
  }
  if (entitlements.hd_download === true) add(t('billing.feature_hd_download'));
  if (entitlements.priority_generation === true) add(t('billing.feature_priority_generation'));
  if (entitlements.commercial_use === true) add(t('billing.feature_commercial_use'));
  if (entitlements.no_watermark === true) add(t('billing.feature_no_watermark'));
  if (entitlements.sharing_enabled === true) add(t('billing.feature_sharing'));
  if (entitlements.batch_import_enabled === true) add(t('billing.feature_batch_import'));
  if (entitlements.remove_branding === true || entitlements.remove_widget_branding === true) {
    add(t('billing.feature_custom_branding'));
  }
  if (entitlements.customer_service_enabled === true) add(t('billing.feature_ai_sales_assistant'));
  if (entitlements.custom_widget_branding === true) add(t('billing.feature_custom_widget'));
  if (entitlements.human_handover_enabled === true) add(t('billing.feature_human_handover'));
  if (entitlements.lead_export_enabled === true) add(t('billing.feature_lead_export'));
  if (entitlements.visual_generation_from_chat === true) add(t('billing.feature_visual_generation_from_chat'));
  if (entitlements.assistant_webhook_enabled === true) add(t('billing.feature_webhooks'));
  if (entitlements.assistant_advanced_analytics_enabled === true) add(t('billing.feature_advanced_analytics'));
  if (entitlements.assistant_advanced_routing_enabled === true) add(t('billing.feature_advanced_routing'));
  if (entitlements.assistant_priority_model_routing_enabled === true) add(t('billing.feature_priority_models'));
  if (entitlements.api_access === true) add(t('billing.feature_api_access'));
  if (entitlements.assistant_addon_allowed === true) add(t('billing.feature_assistant_addon_eligible'));
  return features;
}

function safeBillingFailure(error: unknown, locale?: string): string {
  const state = resolveApplicationFailure(error);
  const t = createAppTranslator('state', locale ?? browserLocaleContext().locale);
  return t(`state.${state.kind}_body`);
}

function browserClient(config: BillingRuntimeConfig): SupabaseClient {
  return createAuthClient({
    applicationCode: config.applicationCode,
    audience: authAudienceForApplication(config.applicationCode),
    supabaseUrl: config.supabaseUrl,
    supabasePublishableKey: config.supabasePublishableKey,
    siteUrl: config.siteUrl,
  });
}

function edgeUrl(config: BillingRuntimeConfig, endpoint: string): string {
  return `${config.supabaseUrl}/functions/v1/${endpoint}`;
}

async function accessToken(supabase: SupabaseClient): Promise<string> {
  const { data } = await supabase.auth.getSession();
  if (!data.session?.access_token) throw new Error(billingText()('billing.sign_in_required'));
  return data.session.access_token;
}

async function api<T>(
  config: BillingRuntimeConfig,
  supabase: SupabaseClient,
  endpoint: string,
  init: RequestInit = {},
  authenticated = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('apikey', config.supabasePublishableKey);
  headers.set('content-type', 'application/json');
  if (authenticated) headers.set('authorization', `Bearer ${await accessToken(supabase)}`);
  const response = await fetch(edgeUrl(config, endpoint), { ...init, headers });
  const payload = (await response.json()) as { data?: T; error?: string };
  if (!response.ok) {
    throw new BillingRequestError(payload.error ?? 'billing_request_failed', response.status);
  }
  return payload.data as T;
}

function usePaddle(config: BillingRuntimeConfig) {
  const [paddle, setPaddle] = useState<Paddle>();
  useEffect(() => {
    if (!config.paddleClientToken) return;
    let active = true;
    void initializePaddle({
      environment: config.paddleEnvironment ?? 'sandbox',
      token: config.paddleClientToken,
    }).then((instance) => {
      if (active && instance) setPaddle(instance);
    });
    return () => {
      active = false;
    };
  }, [config.paddleClientToken, config.paddleEnvironment]);
  return paddle;
}

function Shell({
  brand,
  children,
  locale,
  embedded = false,
}: {
  brand: AppBrand;
  children: React.ReactNode;
  locale?: string | undefined;
  embedded?: boolean | undefined;
}) {
  const t = billingText(locale);
  const palette = brand.palette.light;
  const style = {
    '--billing-canvas': palette.canvas,
    '--billing-surface': palette.surface,
    '--billing-text': palette.text,
    '--billing-muted': palette.muted,
    '--billing-border': palette.border,
    '--billing-primary': palette.primary,
    '--billing-primary-hover': palette.primaryHover,
    '--billing-primary-text': palette.primaryText,
    '--billing-accent': palette.accent,
    '--billing-danger': palette.danger,
    '--billing-radius': brand.radius,
    '--billing-font': brand.typography.ui,
    '--billing-display': brand.typography.display,
  } as CSSProperties;
  const Root = embedded ? 'div' : 'main';
  return (
    <Root className="billing-shell" style={style}>
      {!embedded ? (
      <nav className="billing-nav" aria-label={t('billing.nav_label')}>
        <a className="billing-brand" href="/">
          <span aria-hidden="true">{brand.logo.mark}</span>
          <span>{brand.logo.wordmark}</span>
        </a>
        <div className="billing-nav-links">
          <a href="/pricing">{t('billing.nav_pricing')}</a>
          <a href="/billing">{t('billing.nav_billing')}</a>
        </div>
      </nav>
      ) : null}
      {children}
    </Root>
  );
}

function StateMessage({
  title,
  detail,
  tone = 'neutral',
  headingLevel = 1,
}: {
  title: string;
  detail: string;
  tone?: string;
  headingLevel?: 1 | 2;
}) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  return (
    <section className={`billing-state billing-state--${tone}`} role="status">
      <span className="billing-state-mark" aria-hidden="true">
        {tone === 'danger' ? '!' : tone === 'success' ? '✓' : '·'}
      </span>
      <Heading>{title}</Heading>
      <p>{detail}</p>
    </section>
  );
}

function accountId(): string | null {
  const value = resolveAppAccountId(
    window.location.search,
    window.localStorage.getItem('ai_factory.app_account_id'),
  );
  if (value) window.localStorage.setItem('ai_factory.app_account_id', value);
  return value;
}

function PricingPage({ config, brand, embedded }: Omit<BillingPageProps, 'screen'>) {
  const supabase = browserClient(config);
  const paddle = usePaddle(config);
  const localeContext = browserLocaleContext({
    ...(config.locale !== undefined ? { locale: config.locale } : {}),
    ...(config.timezone !== undefined ? { timezone: config.timezone } : {}),
  });
  const locale = localeContext.locale;
  const t = billingText(locale);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    void api<CatalogProduct[]>(
      config,
      supabase,
      `billing-get-catalog?application_code=${config.applicationCode}`,
      { method: 'GET' },
      false,
    )
      .then(setProducts)
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setLoaded(true));
  }, [config, supabase]);
  const checkout = useCallback(
    async (priceId: string, productType: string) => {
      const scopedAccount = accountId();
      if (!scopedAccount) {
        setError(t('billing.account_required'));
        return;
      }
      setBusy(priceId);
      setError('');
      try {
        const endpoint =
          productType === 'usage_pack' ? 'billing-buy-credit-pack' : 'billing-create-checkout';
        const idempotencyKey = `web-${scopedAccount}-${priceId}-${crypto.randomUUID()}`;
        const data = await api<{
          transactionId: string;
          checkoutUrl: string;
          checkout_order_id: string;
        }>(config, supabase, endpoint, {
          method: 'POST',
          headers: { 'idempotency-key': idempotencyKey },
          body: JSON.stringify({
            application_code: config.applicationCode,
            app_account_id: scopedAccount,
            price_id: priceId,
            checkout_url: `${config.siteUrl}/pricing`,
          }),
        });
        window.sessionStorage.setItem('ai_factory.checkout_order_id', data.checkout_order_id);
        if (paddle) {
          paddle.Checkout.open({
            transactionId: data.transactionId,
            settings: {
              successUrl: `${config.siteUrl}/checkout/result?checkout_order_id=${data.checkout_order_id}`,
            },
          });
        } else {
          window.location.assign(data.checkoutUrl);
        }
      } catch (reason) {
        setError(safeBillingFailure(reason, locale));
      } finally {
        setBusy('');
      }
    },
    [config, locale, paddle, supabase, t],
  );
  return (
    <Shell brand={brand} locale={locale} embedded={embedded}>
      <header className="billing-hero">
        <span className="billing-kicker">{t('billing.pricing_kicker')}</span>
        {embedded ? <h2>{t('billing.pricing_title')}</h2> : <h1>{t('billing.pricing_title')}</h1>}
        <p>{t('billing.pricing_body', { brand: brand.name })}</p>
      </header>
      {error && (
        <div className="billing-alert" role="alert">
          {error}
        </div>
      )}
      {!loaded && !error ? (
        <StateMessage title={t('billing.loading_plans_title')} detail={t('billing.loading_plans_body')} headingLevel={2} />
      ) : null}
      {loaded && products.length === 0 && !error ? (
        <StateMessage
          title={t('billing.no_plans_title')}
          detail={t('billing.no_plans_body')}
          headingLevel={2}
        />
      ) : null}
      <section className="billing-grid" aria-label={t('billing.plans_label', { brand: brand.name })}>
        {products.map((product) => {
          const features = publicEntitlementFeatures(product.entitlements ?? {}, locale, t);
          return (
            <article
              className={`billing-card billing-card--${product.product_type}`}
              key={product.id}
            >
            <div>
              <span className="billing-chip">{productTypeLabel(product.product_type, t)}</span>
              <h2>{product.name}</h2>
              <p>{product.description}</p>
              {features.length ? (
                <ul className="billing-feature-list" aria-label={`${product.name} features`}>
                  {features.map((feature) => <li key={feature}>{feature}</li>)}
                </ul>
              ) : null}
            </div>
            <div className="billing-price-list">
              {product.prices.map((price) => (
                <button
                  className="billing-price-button"
                  disabled={busy === price.id}
                  key={price.id}
                  onClick={() => void checkout(price.id, product.product_type)}
                >
                  <span>
                    <strong>{formatPrice(price.unit_amount, price.currency, locale)}</strong>{' '}
                    {intervalLabel(price.billing_interval, locale)}
                  </span>
                  <span>
                    {busy === price.id
                      ? t('billing.preparing')
                      : price.trial_days
                        ? t('billing.start_trial', { days: price.trial_days })
                        : t('common.continue')}
                  </span>
                </button>
              ))}
            </div>
            </article>
          );
        })}
      </section>
    </Shell>
  );
}

function BillingDashboard({ config, brand }: Omit<BillingPageProps, 'screen'>) {
  const supabase = browserClient(config);
  const localeContext = browserLocaleContext({
    ...(config.locale !== undefined ? { locale: config.locale } : {}),
    ...(config.timezone !== undefined ? { timezone: config.timezone } : {}),
  });
  const t = billingText(localeContext.locale);
  const [subscription, setSubscription] = useState<SubscriptionAccess>();
  const [credits, setCredits] = useState<CreditAccount>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const scopedAccount = accountId();
  const reload = useCallback(async () => {
    if (!scopedAccount) throw new Error(t('billing.account_required'));
    const query = `application_code=${config.applicationCode}&app_account_id=${scopedAccount}`;
    const [nextSubscription, nextCredits] = await Promise.all([
      api<SubscriptionAccess>(config, supabase, `billing-get-subscription?${query}`, {
        method: 'GET',
      }),
      api<CreditAccount>(config, supabase, `billing-get-credits?${query}`, { method: 'GET' }),
    ]);
    setSubscription(nextSubscription);
    setCredits(nextCredits);
  }, [config, scopedAccount, supabase, t]);
  useEffect(() => {
    void reload().catch((reason: Error) => setError(reason.message));
  }, [reload]);
  const portal = async () => {
    if (!scopedAccount) return;
    setBusy(true);
    try {
      const data = await api<{ url: string }>(config, supabase, 'billing-create-portal-session', {
        method: 'POST',
        body: JSON.stringify({
          application_code: config.applicationCode,
          app_account_id: scopedAccount,
        }),
      });
      window.location.assign(data.url);
    } catch (reason) {
      setError(safeBillingFailure(reason, localeContext.locale));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell brand={brand} locale={localeContext.locale}>
      <header className="billing-page-header">
        <div>
          <span className="billing-kicker">{t('billing.account_kicker')}</span>
          <h1>{t('billing.account_title')}</h1>
        </div>
        <button
          className="billing-primary"
          disabled={busy || !subscription?.subscription_id}
          onClick={() => void portal()}
        >
          {busy ? t('billing.opening') : t('billing.manage_in_paddle')}
        </button>
      </header>
      {error && (
        <div className="billing-alert" role="alert">
          {error}
        </div>
      )}
      {!subscription && !error ? (
        <StateMessage
          title={t('billing.loading_account_title')}
          detail={t('billing.loading_account_body')}
        />
      ) : null}
      {subscription ? (
        <section className="billing-dashboard-grid">
          <article className="billing-panel billing-panel--wide">
            <span className={`billing-status billing-status--${subscription.status ?? 'none'}`}>
              {subscription.status ?? t('billing.no_plan')}
            </span>
            <h2>
              {subscription.items.find((item) => item.item_type === 'base_plan')?.product_code ??
                t('billing.choose_plan')}
            </h2>
            <p>
              {subscription.access_allowed
                ? t('billing.access_active')
                : t('billing.access_limited', { reason: subscription.access_reason ?? t('billing.no_plan') })}
            </p>
            <div className="billing-actions">
              <a className="billing-secondary" href="/pricing">
                {t('billing.compare_plans')}
              </a>
              <a className="billing-secondary" href="/billing/cancel">
                {t('billing.cancel_plan')}
              </a>
            </div>
          </article>
          <article className="billing-panel">
            <span className="billing-label">{t('billing.renewal')}</span>
            <strong>
              {subscription.current_period_end
                ? formatDateTime(subscription.current_period_end, localeContext, { dateStyle: 'medium' })
                : '—'}
            </strong>
          </article>
          <article className="billing-panel">
            <span className="billing-label">{t('billing.credits')}</span>
            {credits?.wallets?.map((wallet) => (
              <div className="billing-wallet" key={wallet.credit_type}>
                <strong>{formatNumber(wallet.available_balance, localeContext)}</strong>
                <span>{wallet.credit_type.split(':')[1]?.replaceAll('_', ' ')}</span>
              </div>
            )) ?? <strong>—</strong>}
          </article>
          {subscription.items
            .filter((item) => item.item_type === 'addon')
            .map((item) => (
              <article className="billing-panel" key={item.id}>
                <span className="billing-label">{t('billing.addon')}</span>
                <strong>{item.product_code}</strong>
                <p>{t('billing.addon_body')}</p>
              </article>
            ))}
        </section>
      ) : null}
    </Shell>
  );
}

function CheckoutResult({ config, brand }: Omit<BillingPageProps, 'screen'>) {
  const supabase = browserClient(config);
  const t = billingText(config.locale);
  const [state, setState] = useState<CheckoutStatusPayload>();
  const [error, setError] = useState('');
  const orderId =
    new URLSearchParams(window.location.search).get('checkout_order_id') ||
    window.sessionStorage.getItem('ai_factory.checkout_order_id');
  useEffect(() => {
    if (!orderId) {
      setError(t('billing.checkout_reference_missing'));
      return;
    }
    let stopped = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const next = await api<CheckoutStatusPayload>(
          config,
          supabase,
          `billing-get-checkout-status?checkout_order_id=${orderId}`,
          { method: 'GET' },
        );
        if (stopped) return;
        setState(next);
        if (!['completed', 'failed', 'canceled', 'expired'].includes(next.status))
          timer = window.setTimeout(poll, 2000);
      } catch (reason) {
        if (!stopped)
          setError(safeBillingFailure(reason, config.locale));
      }
    };
    void poll();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [config, orderId, supabase, t]);
  const complete =
    state?.status === 'completed' && state.provider_transaction?.status === 'completed';
  return (
    <Shell brand={brand} locale={config.locale}>
      {error ? (
        <StateMessage title={t('billing.payment_unverified_title')} detail={error} tone="danger" />
      ) : complete ? (
        <StateMessage
          title={t('billing.payment_confirmed_title')}
          detail={t('billing.payment_confirmed_body')}
          tone="success"
        />
      ) : state?.status === 'failed' ? (
        <StateMessage
          title={t('billing.payment_incomplete_title')}
          detail={t('billing.payment_incomplete_body')}
          tone="danger"
        />
      ) : (
        <StateMessage
          title={t('billing.payment_pending_title')}
          detail={t('billing.payment_pending_body')}
        />
      )}
      <div className="billing-centered-actions">
        <a className="billing-primary" href="/billing">
          {t('billing.view_billing')}
        </a>
        <a className="billing-secondary" href="/pricing">
          {t('billing.back_to_pricing')}
        </a>
      </div>
    </Shell>
  );
}

function PastDue({ config, brand }: Pick<BillingPageProps, 'config' | 'brand'>) {
  const t = billingText(config.locale);
  return (
    <Shell brand={brand} locale={config.locale}>
      <StateMessage
        title={t('billing.past_due_title')}
        detail={t('billing.past_due_body')}
        tone="danger"
      />
      <div className="billing-centered-actions">
        <a className="billing-primary" href="/billing">
          {t('billing.review_billing')}
        </a>
      </div>
    </Shell>
  );
}

function CancelPage({ config, brand }: Omit<BillingPageProps, 'screen'>) {
  const supabase = browserClient(config);
  const t = billingText(config.locale);
  const [status, setStatus] = useState<'idle' | 'busy' | 'scheduled'>('idle');
  const [error, setError] = useState('');
  const cancel = async () => {
    const scopedAccount = accountId();
    if (!scopedAccount) {
      setError(t('billing.account_required'));
      return;
    }
    setStatus('busy');
    try {
      await api<ApiPayload>(config, supabase, 'billing-cancel-subscription', {
        method: 'POST',
        body: JSON.stringify({
          application_code: config.applicationCode,
          app_account_id: scopedAccount,
          effective_from: 'next_billing_period',
        }),
      });
      setStatus('scheduled');
    } catch (reason) {
      setError(safeBillingFailure(reason, config.locale));
      setStatus('idle');
    }
  };
  return (
    <Shell brand={brand} locale={config.locale}>
      <section className="billing-cancel-card">
        <span className="billing-kicker">{t('billing.cancel_kicker')}</span>
        <h1>{status === 'scheduled' ? t('billing.cancellation_requested') : t('billing.cancel_title', { brand: brand.name })}</h1>
        <p>
          {status === 'scheduled'
            ? t('billing.cancellation_scheduled_body')
            : t('billing.cancellation_body')}
        </p>
        {error && <div className="billing-alert">{error}</div>}
        <div className="billing-actions">
          <a className="billing-secondary" href="/billing">
            {t('billing.keep_plan')}
          </a>
          {status !== 'scheduled' && (
            <button
              className="billing-danger"
              disabled={status === 'busy'}
              onClick={() => void cancel()}
            >
              {status === 'busy' ? t('billing.scheduling') : t('billing.cancel_at_period_end')}
            </button>
          )}
        </div>
      </section>
    </Shell>
  );
}

export function BillingPage({ screen, config, brand, embedded }: BillingPageProps) {
  if (screen === 'pricing') return <PricingPage config={config} brand={brand} embedded={embedded} />;
  if (screen === 'billing') return <BillingDashboard config={config} brand={brand} />;
  if (screen === 'checkout-result') return <CheckoutResult config={config} brand={brand} />;
  if (screen === 'past-due') return <PastDue config={config} brand={brand} />;
  return <CancelPage config={config} brand={brand} />;
}

export { formatPrice, intervalLabel, resolveAppAccountId } from './model';
